import { BalanceData, Card, CardKind, DamageType, LevelSim, PathShape, TowerArchetype, isBarracks, isBeam, isHex, isMortar } from "@highmarch/domain";
import { icon } from "./iconsKit";
import { pathIconUrl, tierFourArt, towerCardArt } from "./portraits";

/** Card face markup (P1.10.5): road cards draw the shape as tiles, tower cards show the portrait, upgrade cards a gold arrow. */

/** One line per special tile kind (index = TileKind). */
export const TILE_EFFECT: Record<number, string | undefined> = { 1: "ground −30% speed", 2: "flyers untargetable", 3: "+10% points", 4: "burns walkers" };
export const TILE_CLASS = ["", "mud", "bridge", "toll", "firetrap"];
const TILE_FILL = ["#e6c48f", "#a97b52", "#d8c8a8", "#ffd76a", "#ff8a5a"];

/** The shape's tiles as rounded squares on a 64×48 canvas, centred. */
export function shapeGlyph(shape: PathShape): string {
  const xs = shape.tiles.map((t) => t.x), ys = shape.tiles.map((t) => t.y);
  const minX = Math.min(...xs), minY = Math.min(...ys), w = Math.max(...xs) - minX + 1, h = Math.max(...ys) - minY + 1;
  const cell = 16, pad = 3;
  const ox = (64 - w * cell) / 2, oy = (48 - h * cell) / 2;
  const fill = TILE_FILL[shape.tileKind] ?? TILE_FILL[0];
  const rects = shape.tiles.map((t) => `<rect x="${(ox + (t.x - minX) * cell + 1).toFixed(1)}" y="${(oy + (h - 1 - (t.y - minY)) * cell + 1).toFixed(1)}" width="${cell - pad}" height="${cell - pad}" rx="2" fill="${fill}" stroke="#4a2a08" stroke-width="1.5"/>`).join("");
  return `<svg width="64" height="48" viewBox="0 0 64 48" aria-hidden="true">${rects}</svg>`;
}

/**
 * A tower card's role tags (QoL P4): what it can shoot (air and ground, ground only, or neither — support), what its
 * shot does (splash, slow, burn, pierce, chain, shred, aura; plain single-target otherwise), and whether it hits the
 * flyers the next wave brings. Read from the tower's data, so a new tower gets tags without a line here.
 */
export interface TowerTags { readonly reach: "AIR+GND" | "GROUND" | "AIR" | "SUPPORT"; readonly role: string; readonly flyersNext: boolean }

export function towerTags(t: TowerArchetype, flyersNext: boolean): TowerTags {
  const reach = t.targetsAir && t.targetsGround ? "AIR+GND" : t.targetsGround ? "GROUND" : t.targetsAir ? "AIR" : "SUPPORT";
  // the TOWERS_V2 towers name what they do: lob, block, beam, curse; Frost's burst is area, but SLOW is what it does
  const role = isMortar(t) ? "LOB" : isBarracks(t) ? "BLOCK" : isBeam(t) ? "BEAM" : isHex(t) ? "CURSE"
    : t.auraDamageBonus.isPositive() ? "AURA" : t.slowFraction.isPositive() ? "SLOW" : t.splashRadius.isPositive() ? "SPLASH"
    : t.damageType === DamageType.Burn || t.burnDamagePerSecond.isPositive() ? "BURN" : t.pierceCount > 0 ? "PIERCE"
    : t.chainTargets > 0 ? "CHAIN" : t.shredPerHit.isPositive() ? "SHRED" : "SINGLE";
  return { reach, role, flyersNext: flyersNext && t.targetsAir };
}

/** Whether the next wave (wave 1 during the opening draft) brings flyers: the tower cards' "hits flyers" tag. */
export function flyersInNextWave(sim: LevelSim): boolean {
  const next = Math.min(sim.stages, sim.stage + 1);
  return sim.previewStage(next).some((e) => sim.balance.enemies[e.archetype].isAir);
}

function tagsHtml(tags: TowerTags): string {
  return `<span class="tags"><i class="reach">${tags.reach}</i><i class="role">${tags.role}</i></span>${tags.flyersNext ? `<span class="flyers">HITS FLYERS</span>` : ""}`;
}

export function cardClass(balance: BalanceData, card: Card): string {
  if (card.kind === CardKind.Path) return `road ${TILE_CLASS[balance.cards.shapes[card.index].tileKind] ?? ""}`;
  // Tier 4 (docs/TOWERS_V2.md): the gold special cards, keyed by tower so the guide and CSS can find them
  if (card.kind === CardKind.Ascend) return `special ascend t-${balance.towers[card.index].id}`;
  if (card.kind === CardKind.Skill) { const b = balance.branches.branches[balance.branches.skills[card.index].branchIndex]; return `special skill t-${b.towerId} b-${b.id}`; }
  if (card.kind === CardKind.Move) return "move";
  return card.kind === CardKind.Upgrade ? "upgrade" : `tower t-${balance.towers[card.index].id}`;
}

/** The Move card's art (M55): a tower with an arrow sweeping over it to a new square. */
const MOVE_ART = `<svg width="72" height="60" viewBox="0 0 72 60" aria-hidden="true" fill="none" stroke-linecap="round" stroke-linejoin="round">`
  + `<rect x="3" y="49" width="20" height="9" rx="2" fill="rgba(0,0,0,0.25)" stroke="#fff8e6" stroke-width="1.6" stroke-dasharray="3.2 2.6"/>`
  + `<rect x="47" y="49" width="20" height="9" rx="2" fill="#ffd75c" stroke="#4a2a08" stroke-width="1.6"/>`
  + `<path d="M50 49V31l-2.5-1.2v-5l5 1.5 3.3-1.5 3.2 1.5 3.3-1.5 5 1.5v5L65 31v18z" fill="#fff8e6" stroke="#4a2a08" stroke-width="1.8"/>`
  + `<path d="M55 49v-6h5v6" stroke="#4a2a08" stroke-width="1.6"/>`
  + `<path d="M13 45C13 31 24 24 38 27" stroke="#ffd75c" stroke-width="4"/>`
  + `<path d="M33 21.5l7 5.8-6.4 6.2" stroke="#ffd75c" stroke-width="4"/></svg>`;

/**
 * `delay` is the points a pick of this card adds to the next offer (DESIGN §4.4, LevelSim.pickDelay); when it is not
 * zero the card wears it as a tag, so taking the tower reads as the trade it is.
 */
export function cardFace(balance: BalanceData, card: Card, placedOfShape: (shapeIndex: number) => number, delay = 0, flyersNext = false): string {
  const tag = delay > 0 ? `<span class="delay">next hand +${delay} pts</span>` : "";
  if (card.kind === CardKind.Upgrade) {
    return `<div class="kind">UPGRADE</div>${tag}<div class="art">${icon("chev", 44, "#ffd75c", 3)}</div><b>+1 tier</b><small>tower or fortress</small>`;
  }
  if (card.kind === CardKind.Ascend || card.kind === CardKind.Skill) return specialFace(balance, card, tag);
  if (card.kind === CardKind.Move) return `<div class="kind">MOVE</div>${tag}<div class="art">${MOVE_ART}</div><b>Any tower</b><small>to a new square</small>`;
  if (card.kind === CardKind.Path) {
    const s = balance.cards.shapes[card.index];
    const effect = TILE_EFFECT[s.tileKind];
    const cap = s.maxPerLevel > 0 ? ` · ${placedOfShape(card.index)}/${s.maxPerLevel}` : "";
    // a special tile trades on the road pace (DESIGN §4.7: after a Bridge the next road card comes sooner, after Mud
    // later), so the card names the price
    const cost = s.paceCost > s.tiles.length ? " · next road later" : s.paceCost < s.tiles.length ? " · next road sooner" : "";
    const art = s.id ? `<img decoding="sync" src="${pathIconUrl(s.id)}" alt="" style="height:64px;display:block;filter:drop-shadow(0 3px 3px rgba(0,0,0,0.45))">` : shapeGlyph(s);
    return `<div class="kind">${effect ? "SPECIAL" : "ROAD"}</div>${tag}<div class="art">${art}</div><b>${s.name}</b><small>${effect ? effect + cost + cap : `${s.tiles.length} tiles`}</small>`;
  }
  const t = balance.towers[card.index];
  const { art, back } = towerCardArt(t.id, 88, icon("tower", 44, "#fff8e6"));
  return `${back}<div class="kind">TOWER</div>${tag}${tagsHtml(towerTags(t, flyersNext))}<div class="art">${art}</div><b>${t.name}</b><small>${cardRangeLine(t)}</small>`;
}

/** The tower card's small line (one line on the card): its range — the Mortar's from its blind spot out — or the Barracks' squad. */
export function cardRangeLine(t: TowerArchetype): string {
  if (isMortar(t)) return `range ${t.minRange.toDecimalString(1)}–${t.range.toDecimalString(1)}`;
  if (isBarracks(t)) return `${t.soldiers} soldiers`;
  return `range ${t.range.toDecimalString(1)}`;
}

/**
 * The tier-4 special card (docs/TOWERS_V2.md "Tier 4"): ASCEND — the tower's tier-3 art in the gold "IV" frame and "choose
 * a branch"; SKILL — the branch's art (its own when it ships) and the skill's name over its branch's.
 */
export function specialFace(balance: BalanceData, card: Card, tag = ""): string {
  if (card.kind === CardKind.Ascend) {
    const t = balance.towers[card.index];
    return `<div class="kind">ASCEND</div>${tag}<div class="art">${tierFourArt(t.id, null, 70)}</div><b>${t.name} IV</b><small>choose a branch</small>`;
  }
  const s = balance.branches.skills[card.index], b = balance.branches.branches[s.branchIndex];
  return `<div class="kind">SKILL</div>${tag}<div class="art">${tierFourArt(b.towerId, b.id, 70)}</div><b>${s.name}</b><small>${b.name}</small>`;
}
